// src/components/users/UserProfile.jsx
import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { FormInput, FormSelect, FormTextArea } from '../forms';
import { User, Mail, Phone, Book, Calendar, MapPin, Users } from 'lucide-react';

export const UserProfile = ({ userId }) => {
  const { get, put } = useApi();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await get(userId ? `/users/${userId}` : '/auth/me');
        setUser(response.data.data.user);
        setFormData(response.data.data.user);
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };

    fetchUser();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await put('/auth/profile', formData);
      setUser(response.data.data.user);
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      setErrors(error.response?.data?.errors || {});
    }
  };
  
  if (!user) return <div>Loading...</div>;

  // const canEdit = currentUser.id === user.id || currentUser.role === 'admin';
  const canEdit = 
  currentUser.id === user.id || 
  ["admin", "receptionist", "counselor", "hod"].includes(currentUser.role);


  return (
    <div className="max-w-4xl mx-auto bg-white shadow rounded-lg">
      {/* Profile Header */}
      <div className="px-6 py-4 border-b flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900">User Profile</h2>
        {canEdit && (
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-4 py-2 text-sm font-medium text-uzima-maroon-500 hover:text-uzima-maroon-400"
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        )}
      </div>

      <div className="p-6">
        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FormInput
                label="Full Name"
                value={formData.fullName || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  fullName: e.target.value 
                }))}
                error={errors.fullName}
                required
              />
              <FormInput
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
                error={errors.email}
                required
              />
              <FormInput
                label="Phone"
                value={formData.phone || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                phone: e.target.value 
                }))}
                error={errors.phone}
              />
              <FormInput
                label="Username"
                value={formData.username || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  username: e.target.value
                }))}
                error={errors.username}
                required
              />
              {user.role === 'student' && (
                <>
                  <FormInput
                    label="Course Taken"
                    value={formData.profile?.courseTaken || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      profile: { ...prev.profile, courseTaken: e.target.value }
                    }))}
                    error={errors.profile?.courseTaken}
                  />
                  <FormSelect
                    label="Year of Study"
                    value={formData.profile?.yearOfStudy || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      profile: { ...prev.profile, yearOfStudy: e.target.value }
                    }))}
                    options={[
                      { value: '1', label: 'First Year' },
                      { value: '2', label: 'Second Year' },
                      { value: '3', label: 'Third Year' },
                      { value: '4', label: 'Fourth Year' },
                      { value: '5', label: 'Fifth Year' }
                    ]}
                    error={errors.profile?.yearOfStudy}
                  />
                </>
              )}
            </div>

            {/* Next of Kin (for students) */}
            {user.role === 'student' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Next of Kin</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Name"
                    value={formData.nextOfKin?.name || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      nextOfKin: { ...prev.nextOfKin, name: e.target.value }
                    }))}
                    error={errors.nextOfKin?.name}
                    required
                  />
                  <FormInput
                    label="Relationship"
                    value={formData.nextOfKin?.relation || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      nextOfKin: { ...prev.nextOfKin, relation: e.target.value }
                    }))}
                    error={errors.nextOfKin?.relation}
                    required
                  />
                  <FormInput
                    label="Phone"
                    value={formData.nextOfKin?.phone || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      nextOfKin: { ...prev.nextOfKin, phone: e.target.value }
                    }))}
                    error={errors.nextOfKin?.phone}
                    required
                  />
                  <FormInput
                    label="Email"
                    type="email"
                    value={formData.nextOfKin?.email || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      nextOfKin: { ...prev.nextOfKin, email: e.target.value }
                    }))}
                    error={errors.nextOfKin?.email}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-uzima-maroon-500 text-white rounded-md hover:bg-uzima-maroon-600"
              >
                Save Changes
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            {/* Basic Information Display */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <User className="h-4 w-4 mr-1" />
                  Full Name
                </div>
                <p className="font-medium text-gray-900">{user.fullName}</p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </div>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Phone className="h-4 w-4 mr-1" />
                  Phone
                </div>
                <p className="font-medium text-gray-900">{user.phone || 'Not provided'}</p>
                
              </div>
              
              {user.role === 'student' && (
                <>
                  <div>
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <Book className="h-4 w-4 mr-1" />
                      Course Taken
                    </div>
                    <p className="font-medium text-gray-900">{user.studentInfo?.courseTaken}</p>
                  </div>
                  <div>
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      Year of Study
                    </div>
                    <p className="font-medium text-gray-900">{user.profile?.yearOfStudy}</p>
                  </div>
                </>
              )}
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <User className="h-4 w-4 mr-1" />
                  Full Name
                </div>
                <p className="font-medium text-gray-900">{user.fullName}</p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <User className="h-4 w-4 mr-1" />
                  Username
                </div>
                <p className="font-medium text-gray-900">{user.username}</p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </div>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
              <div>
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Phone className="h-4 w-4 mr-1" />
                  Phone
                </div>
                <p className="font-medium text-gray-900">{user.phone || 'Not provided'}</p>
              </div>
            </div>

            {/* Next of Kin Display */}
            {user.role === 'student' && user.nextOfKin && (
              <div className="pt-6 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Next of Kin</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Name</div>
                    <p className="font-medium text-gray-900">{user.nextOfKin.name}</p>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Relationship</div>
                    <p className="font-medium text-gray-900">{user.nextOfKin.relation}</p>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Phone</div>
                    <p className="font-medium text-gray-900">{user.nextOfKin.phone}</p>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Email</div>
                    <p className="font-medium text-gray-900">{user.nextOfKin.email || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};